import {LitElement, html, css} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import {unsafeHTML as litUnsafeHTML} from 'lit/directives/unsafe-html.js';
import {replaceLegacyFonts} from './replace-legacy-fonts.js';
import {styleMap} from 'lit/directives/style-map.js';
import apiService from '../utils/apiService';
import config from '../config/config';

export class ResearchLitElement extends LitElement {
    static get properties() {
        return {
            intersectionRatio: {
                type: Number,
            },
            lang: {
                type: String,
            },
        };
    }

    constructor() {
        super();
        this.intersectionRatio = 0.1;

        const $that = this;
        this.$ = new Proxy(
            {},
            {
                get(target, name, receiver) {
                    return $that.shadowRoot.getElementById(name);
                },
            }
        );
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('popstate', this._onPopState.bind(this));
        this.lang = localStorage.getItem('language') || 'ka';
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('popstate', this._onPopState);
    }

    update(changedProperties) {
        const propertyConfig = this.constructor.properties;
        changedProperties.forEach((value, key) => {
            if (key && propertyConfig[key]) {
                if (propertyConfig[key].observer) {
                    if (this[propertyConfig[key].observer]) {
                        this[propertyConfig[key].observer].apply(this, [this[key], value, key]);
                    } else {
                        console.warn(`${propertyConfig[key].observer} is not defined`, this);
                    }
                }
                if (propertyConfig[key].notify) {
                    this._fireChangeEvent(key, this[key], value);
                }
            }
        });
        return super.update(changedProperties);
    }

    _fireChangeEvent(property, newValue, oldValue) {
        const event = property.replace(/([A-Z])/g, '-$1').toLowerCase() + '-changed';
        this.dispatchEvent(new CustomEvent(event, {
            detail: {
                value: newValue,
                oldValue: oldValue,
            },
            composed: false,
            bubbles: false,
        }));
    }

    _onPopState() {
        const drawers = this.shadowRoot.querySelectorAll('bd-drawer');
        drawers.forEach((drawer) => {
            if (drawer.isOpen) {
                drawer.close();
            }
        });
        const modals = document.querySelectorAll('bd-modal');
        modals.forEach((modal) => {
            modal.close();
        });
    }

    apiCall(url, method = 'GET', data = {}, isText = false, contentType = 'application/json') {
        return apiService.execute({
            url,
            method,
            data,
            isText,
            contentType,
        });
    }

    dispatchCustomEvent(name, detail = null, bubbles = false, composed = false) {
        const event = new CustomEvent(name, {
            bubbles: bubbles,
            composed: composed,
            detail: detail,
        });
        this.dispatchEvent(event);
    }

    initLocations() {
        let script = document.createElement('script');
        script.src = config.mapApiUrl;
        script.async = true;
        document.body.appendChild(script);
        script = document.createElement('script');
        script.src = config.mapMarkerUrl;
        script.async = true;
        document.body.appendChild(script);
    }

    sendAnalyticsEvent({
        eventAction,
        eventLabel = '',
        eventName,
        isNonInteraction = true,
        customParams = {},
        sendToFB = false,
    }
    ) {
        if (typeof window === 'undefined' || !eventAction || !eventName) {
            return;
        }

        const params = {
            event_action: eventAction,
            event_label: eventLabel,
            non_interaction: isNonInteraction,
            ...customParams,
        };

        window.gtag?.('event', eventName.toUpperCase(), params);

        if (sendToFB) {
            if (typeof window.fbq === 'function') {
                window.fbq('track', eventLabel, params);
            }
        }
    }

    redirectTo(path, title = null) {
        if (path.substr(0, 2) === './') {
            const pathname = window.location.pathname;
            path = pathname + path;
            path = path.replace('/./', '/');
            path = path.replace('//', '/');
        }
        window.history.pushState({}, title, path);
        window.dispatchEvent(new CustomEvent('app-router-updated'));
    }

    sendCustomEvent(name, value, bubbles = true, composed = true) {
        this.dispatchEvent(new CustomEvent(name, {
            detail: value,
            composed: composed,
            bubbles: bubbles,
        }));
    }
}

/**
 * Drop-in replacement for lit's `unsafeHTML` that normalizes legacy CMS font
 * families (MarkGEO / MarkGEOCAPS / BOG Headline) to the current `BOG` font
 * before rendering — caps families also get the uppercase treatment.
 * Every component imports `unsafeHTML` from here, so the rewrite is universal.
 * @param {*} value raw HTML string from CMS/html fields
 * @return {import('lit').DirectiveResult} lit directive result
 */
const unsafeHTML = (value) => litUnsafeHTML(replaceLegacyFonts(value));

export {html, css, unsafeHTML, ifDefined, styleMap};
